import { Authentication } from "./auth";
import { CognitoAuthOptions, MfaStatusEnum, PasswordChangeRequest, User } from "../types";
import { Config } from "@/shared/config";
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  CognitoRefreshToken,
  CognitoAccessToken,
  CognitoIdToken,
  NodeCallback,
  IMfaSettings
} from "amazon-cognito-identity-js";
import _ from "lodash";
import * as AWS from "aws-sdk/global";
import { markUserAsVerified, recoverUser } from "./userService";

const userNotFoundCode = "UserNotFoundException";

// invoke callback as promise is a generic function that returns a promise of type T
// it's argument is any function that accepts a callback
// the callback has two arguments, error and result
const invokeCallbackAsPromise = <T>(wrappedFunction: (callback: NodeCallback<any, T>) => any) => {
  return new Promise<T>((resolve, reject) => {
    wrappedFunction((err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
};

const promiseOfUserGetSession = (user: CognitoUser) => invokeCallbackAsPromise<CognitoUserSession>(user.getSession.bind(user));
const promiseOfUserAttributes = (user: CognitoUser) =>
  invokeCallbackAsPromise<CognitoUserAttribute[]>(user.getUserAttributes.bind(user));
const promiseOfUserRefreshSession = (user: CognitoUser, refreshToken: CognitoRefreshToken) =>
  invokeCallbackAsPromise<CognitoUserSession>(user.refreshSession.bind(user, refreshToken));

const isLambaColdStartPenalty = err =>
    err.code === "UnexpectedLambdaException"
    && err.statusCode === 400
    && (err.message || "").indexOf("failed with error Socket timeout while invoking Lambda function") > -1;

const promiseOfAuthenticateUser = (user: CognitoUser, options:
  {
    username: string
    password: string,
    newPassword?: string,
    retriesUsed?: number,
    mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void
  }) => {
    return new Promise<CognitoUserSession | { newPasswordRequired: boolean }>((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: options.username,
        Password: options.password
      });

      try {
        user.authenticateUser(authDetails, {
          onSuccess: sesh => {
            resolve(sesh);
          },
          onFailure: err => {
            reject(err);
          },
          newPasswordRequired: (_, requiredAttributes) => {
            if (options.newPassword) {
              user.completeNewPasswordChallenge(
                  options.newPassword,
                  requiredAttributes,
                  {
                    onSuccess: sesh => resolve(sesh),
                    onFailure: err => reject(err),
                    mfaRequired: _ => {
                      if (options.mfaFunc) {
                        options.mfaFunc(MfaStatusEnum.Sms, user);
                      }

                      resolve(null);
                    }
                  });
            } else {
              return reject({newPasswordRequired: true});
            }
          },
          totpRequired: _ => {
            if (options.mfaFunc) {
              options.mfaFunc(MfaStatusEnum.Totp, user);
            }

            resolve(null);
          },
          mfaRequired: _ => {
            if (options.mfaFunc) {
              options.mfaFunc(MfaStatusEnum.Sms, user);
            }

            resolve(null);
          }
        });
      }
      catch (e) {
        if (typeof (e) === "object"
            && e.toString().indexOf("not authenticated") > 0) {
          return;
        }
      }
    }).catch(err => {
      if (err.code === userNotFoundCode) {
        err.message = "Incorrect username or password";
      }
      if (isLambaColdStartPenalty(err)) {
        const retriesUsed = typeof (options.retriesUsed) === "undefined" ? 0 : <number> options.retriesUsed;
        if (retriesUsed < 3) {
          return promiseOfAuthenticateUser(user, {...options, retriesUsed: retriesUsed + 1});
        }
      }
      throw err;
    });
};

const promiseOfCurrentUser = (pool: CognitoUserPool) =>
  new Promise<CognitoUser>((resolve, reject) => {
    const currentUser = pool.getCurrentUser();
    if (!currentUser) {
      reject(new Error("getCurrentUser is null"));
    } else {
      resolve(currentUser);
    }
  });

export class CognitoAuth implements Authentication {
  private identityPool: string;
  private userPool;

  constructor (options: CognitoAuthOptions) {
    this.userPool = new CognitoUserPool({
      UserPoolId: options.userPoolId,
      ClientId: options.clientId
    });
    this.identityPool = options.identityPoolId;
  }

  private createAuthenticationDetails (username: string, password: string) {
    return new AuthenticationDetails({
      Username: username,
      Password: password,
    });
  }

  createCognitoUser (username: string) {
    return new CognitoUser({
      Username: username,
      Pool: this.userPool
    });
  }

  private createCognitoUserSession (accessToken: string, idToken: string, refreshToken: string) {
    return new CognitoUserSession({
      AccessToken: new CognitoAccessToken({AccessToken: accessToken}),
      IdToken: new CognitoIdToken({IdToken: idToken}),
      RefreshToken: new CognitoRefreshToken({RefreshToken: refreshToken})
    });
  }

  accessToken (): Promise<string> {
    const user = this.userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      if (!user) {
        resolve("");
        return;
      }

      user.getSession(() => {
        const session = user.getSignInUserSession();

        if (!session) {
          reject();
          return;
        }

        resolve(session.getAccessToken().getJwtToken());
      });
    });
  }

  getAwsCredentials (): Promise<string> {
    const promise: Promise<string> = new Promise<string>((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (cognitoUser === null) {
        reject("User cannot be null");
        return;
      }
      cognitoUser.getSession((err, session) => {
        if (err) {
            reject(err);
            return;
        }
        const logins = {};
        logins["cognito-idp." + Config.Region + ".amazonaws.com/" + this.userPool.getUserPoolId()] =
          session.getIdToken().getJwtToken();

        AWS.config.region = Config.Region;
        AWS.config.credentials = new AWS.CognitoIdentityCredentials({
          IdentityPoolId: this.identityPool,
          Logins: logins
        });
        const credentials = (<AWS.CognitoIdentityCredentials>AWS.config.credentials);
        if (credentials.needsRefresh()) {
          credentials.clearCachedId();
          credentials.get(err => {
            if (err) {
              reject(err);
              return;
            }
            resolve(credentials.identityId);
          });
        } else {
          credentials.get(err => {
            if (err) {
              alert(err);
              reject(err);
              return;
            }
            resolve(credentials.identityId);
          });
        }
      });
    });

    return promise;
  }

  mapCognitoSessionToGroups (session: CognitoUserSession): string[] {
    const idToken: any = session.getIdToken();
    const payload = idToken.payload; // aws did not put this in the type definition
    return payload["cognito:groups"] || [];
  }

  mapSessionAndAttributesToUser (session: CognitoUserSession, result: CognitoUserAttribute[]): User {
    const firstNameAttr = result.find(attr => attr.getName() === "given_name");
    const lastNameAttr = result.find(attr => attr.getName() === "family_name");
    const emailAttr = result.find(attr => attr.getName() === "email");
    const emailVerifiedAttr = result.find(attr => attr.getName() === "email_verified");
    const phoneAttr = result.find(attr => attr.getName() === "phone_number");

    const groups = this.mapCognitoSessionToGroups(session);

    let firstName = "";
    let lastName = "";
    let email = "";

    if (firstNameAttr) {
      firstName = firstNameAttr.getValue();
    }

    if (lastNameAttr) {
      lastName = lastNameAttr.getValue();
    }

    if (emailAttr) {
      email = emailAttr.getValue();
    }

    const user: User = {
      displayName: `${firstName} ${lastName}`,
      groups,
      firstName,
      lastName,
      email,
      username: email
    };

    if (phoneAttr) {
      user.phone = phoneAttr.getValue();
    }

    return user;
  }

  async getUser (): Promise<{ username: string | null }> {
    const user = this.userPool.getCurrentUser();
    return { username: user?.getUsername() ?? null };
  }

  loginInitializePassword (username: string, initialPassword: string, requestedPassword: string,
                           mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void) {
    return this.setInitialPassword(username, initialPassword, requestedPassword, mfaFunc);
  }

  setSignInUserSession (username: string, accessToken: string, idToken: string, refreshToken: string): Promise<CognitoUser> {
    const cognitoUser = this.createCognitoUser(username);
    const cognitoUserSession = this.createCognitoUserSession(accessToken, idToken, refreshToken);

    cognitoUser.setSignInUserSession(cognitoUserSession);

    return promiseOfCurrentUser(this.userPool);
  }

  postLoginProcess (promise: Promise<CognitoUserSession>, username: string,
                    requiredGroups: ReadonlyArray<string>, cognitoUser: CognitoUser): Promise<User> {
        return promise.then((session: CognitoUserSession) =>
        promiseOfUserAttributes(cognitoUser)
            .then(userAttributes => ({session, userAttributes}))
        )
        .then(({ session, userAttributes }) => ({
          user: this.mapSessionAndAttributesToUser(session, userAttributes),
          userAttributes
        }))
        .then(({ user, userAttributes }) => {
          const emailAttr = userAttributes.find(attr => attr.getName() === "email");
          const email = emailAttr && emailAttr.getValue();
          const emailVerifiedAttr = userAttributes.find(attr => attr.getName() === "email_verified");

          if (!emailVerifiedAttr && email) {
            return markUserAsVerified(username)
                .then(() => user);
          }
          return user;
        })
        .then((user: User) => {
          if (requiredGroups.length === 0) {
            return user;
          }

          if (user.groups.some(userGroup => requiredGroups.includes(userGroup))) {
            return user;
          }

          let loginUrl = "https://user.bendhsa.com/";
          const groups = user.groups.length ? user.groups : ["employee"];
          switch (groups[0]) {
            case "admin":
              loginUrl = "https://admin.bendhsa.com/";
              break;
            case "employer":
              loginUrl = "https://employer.bendhsa.com/";
              break;
          }

          return Promise.reject<User>({ loginUrl, missingRequiredGroup: true });
        });
  }

  login (username: string, password: string, requiredGroups: ReadonlyArray<string>,
         mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void)
      : Promise<User> {

    /*const mfaFunc2 = (s: MfaStatusEnum, u: CognitoUser) => {
      commit("setMfaStatus", s);
      commit("mfa/setCognitoUser", u, { root: true });
    };*/
    const cognitoUser = this.createCognitoUser(username);
    const promise = promiseOfAuthenticateUser(cognitoUser, { username, password, mfaFunc });
    return this.postLoginProcess(promise, username, requiredGroups, cognitoUser);
  }

  forceRefreshSession (): Promise<User> {
    return promiseOfCurrentUser(this.userPool)
      .then(cognitoUser =>
         promiseOfUserGetSession(cognitoUser)
          .then(session => ({ cognitoUser, session }))
      )
      .then(({ cognitoUser, session }) =>
        promiseOfUserRefreshSession(cognitoUser, session.getRefreshToken())
          .then(refreshedSession => ({ cognitoUser, refreshedSession }))
      )
      .then(({ cognitoUser, refreshedSession }) =>
         promiseOfUserAttributes(cognitoUser)
          .then(userAttributes => ({ userAttributes, refreshedSession }))
      ).then(({ refreshedSession, userAttributes }) =>
        this.mapSessionAndAttributesToUser(refreshedSession, userAttributes));
  }

  logout () {
    const user = this.userPool.getCurrentUser();

    if (!user) {
      return;
    }

    try {
      // callback returns (error, session).
      user.getSession((_, session) => {
        return this.userPool.client.request(
          "RevokeToken",
          {
            Token: session.getRefreshToken().getToken(),
            ClientId: this.userPool.getClientId(),
          },
          _ => {
            user.signOut();
          }
        );
      });
    } catch {
      user.signOut();
    }
  }

  confirmPassword (username: string, verificationCode: string, password: string): Promise<any> {
    const cognitoUser = this.createCognitoUser(username);

    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmPassword(verificationCode, password, {
        onSuccess () {
          resolve();
        },
        onFailure (err) {
          reject(err);
        }
      });
    });
  }

  verifyEmail (verificationCode?: string): Promise<void> {
    let cognitoUser: CognitoUser = null;

    return new Promise<void>((resolve, reject) => {
      cognitoUser = this.userPool.getCurrentUser();
      if (cognitoUser === null) {
        reject(new Error("user is required"));
      }
      cognitoUser.getSession(err => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    })
    .then(() => {
      return new Promise<void>((resolve, reject) => {

        const callbackObject = {
          onSuccess: () => {
            resolve();
          },
          onFailure: err => {
            reject(err);
          },
          inputVerificationCode: null
        };

        if (verificationCode) {
          cognitoUser.verifyAttribute("email", verificationCode, callbackObject);
        } else {
          cognitoUser.getAttributeVerificationCode("email", callbackObject);
        }
      });
    });
  }

  setInitialPassword (username: string, oldPassword: string, newPassword: string, mfaFunc) {
    const cognitoUser = this.createCognitoUser(username);
    return promiseOfAuthenticateUser(cognitoUser, { username, password: oldPassword, newPassword, mfaFunc });
  }

  resetPassword (username: string): Promise<any> {
    const cognitoUser = this.createCognitoUser(username);

    return new Promise<void>((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess (data) {
          resolve(data);
        },
        onFailure (err) {
          const code = (_.get(err, "code", "")) as string;
          if (code === userNotFoundCode) {
            // use the success path to hide the default cognito error message
            resolve();
            return;
          }
          reject(err);
        }
      });
    });
  }

  changePassword (request: PasswordChangeRequest) {
    return new Promise<void>((resolve, reject) => {
      const cognitoUser = this.userPool.getCurrentUser();
      if (!cognitoUser) {
        reject(new Error("User is not authenticated"));
        return;
      }

      cognitoUser.getSession(err => {
        if (err) {
          reject(err);
          return;
        }

        cognitoUser.changePassword(
          request.currentPassword,
          request.newPassword,
          error => {
            if (error) {
              reject(error);
              return;
            }

            resolve();
          }
        );
      });
    });
  }

  autoRecoverUser (email: string) {
    return recoverUser(email);
  }

  setMFAPreference (cognitoUser: CognitoUser, smsMfaSettings: IMfaSettings | null,
    softwareTokenMfaSettings: IMfaSettings | null): Promise<string> {
    return new Promise((resolve, reject) => {
      cognitoUser.setUserMfaPreference(smsMfaSettings, softwareTokenMfaSettings, (err, result) => {
        if (err) {
            reject(err);
        }
        resolve(result);
      });
    });
  }

  associateToken (cognitoUser: CognitoUser): Promise<string> {
    // Get the secret code to create the QR code
    return new Promise((resolve, reject) => {
      cognitoUser.associateSoftwareToken({
        onFailure: err => {
          reject(err);
        },
        associateSecretCode: secretCode => {
          resolve(secretCode);
        },
      });
    });
  }

  verifyToken (cognitoUser: CognitoUser, token: string): Promise<CognitoUserSession> {
    return new Promise((resolve, reject) => {
      cognitoUser.verifySoftwareToken(token, "TOTP device", {
        onSuccess: session => {
          resolve(session);
        },
        onFailure: err => {
          reject(err);
        }
      });
    });
  }

  authenticateCognitoUser (username: string, password: string,
                           mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void): Promise<CognitoUser> {
    return new Promise<CognitoUser>((resolve, reject) => {
      const cognitoUser = this.createCognitoUser(username);
        promiseOfAuthenticateUser(cognitoUser, { username, password, mfaFunc })
        .then(() => {
          resolve(cognitoUser);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  updateUserAttribute (cognitoUser: CognitoUser, attributeName: string, attributeValue: string): Promise<void> {
    const attributeList = [];
    const attribute = {
        Name: attributeName,
        Value: attributeValue,
    };
    attributeList.push(attribute);

    return new Promise((resolve, reject) => {
      cognitoUser.updateAttributes(attributeList, (err, result) => {
        if (err) {
            reject(err);
        }
        resolve();
      });
    });
  }

  sendPhoneVerification (cognitoUser: CognitoUser): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser.getAttributeVerificationCode("phone_number", {
        onSuccess: () => {
            resolve();
        },
        onFailure: err => {
            reject(err);
        },
        inputVerificationCode: () => {
            resolve();
        }
      });
    });
  }

  verifyPhone (cognitoUser: CognitoUser, verificationCode: string): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser.verifyAttribute("phone_number", verificationCode, {
        onFailure: err => {
          reject(err);
        },
        onSuccess: success => {
          resolve();
        }
      });
    });
  }

  getPhone (cognitoUser: CognitoUser): Promise<string> {
    return new Promise((resolve, reject) => {
      promiseOfUserAttributes(cognitoUser)
        .then(userAttributes => {
          const phoneNumber = userAttributes.find(attr => attr.getName() === "phone_number");
          if (phoneNumber === undefined) {
            resolve(null);
          }

          resolve(phoneNumber.getValue());
        }).catch(err => {
          reject(err);
        });
    });
  }

  isPhoneVerified (cognitoUser: CognitoUser): Promise<boolean> {
    return new Promise((resolve, reject) => {
      promiseOfUserAttributes(cognitoUser)
        .then(userAttributes => {
          const verified = userAttributes.find(attr => attr.getName() === "phone_number_verified");
          if (verified === undefined) {
            resolve(null);
          }
          resolve(verified !== undefined && verified.getValue() === "true");
        }).catch(err => {
          reject(err);
        });
    });
  }
}